import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import RecordRTC, { StereoAudioRecorder } from "recordrtc";
import blinkscribeapi from "../api/blinkscribeapi";
import api from "../api/api";
import careiqapi from "../api/careiqapi";
import { saveAudioInIndexDB } from "../offlinestreaming/offlineBlobHandler";
import { generateFilename } from "../utils/generatefilename";
import { eventBus } from "../utils/eventBus";
import { pauseControlFrame, micLabelQueryParam, createRateWindow } from "../utils/liveScribeMic";
import { localDateStr, rowsOf, snapshotMaxId, pickCompletedRow, rowAppointmentId } from "../utils/recordingCompletion";
import { guardRecordingVisit } from "../components/VisitConsent/recordingVisitGuard";
import { lockRecordingTarget, unlockRecordingTarget } from "../utils/recordingTarget";
import { chooseCombineTarget } from "../components/VisitConsent/combineChoice";
import { requestChartVisit } from "../utils/chartVisitSwitch";
import { isVisitRefusal, refusalMessage } from "../utils/visitGuard";
import { setRecordingState as setDeferredNavigationState } from "../utils/deferredNavigation";

// Completion poll cadence: fast for the first 2 minutes, then slower up to 10
// minutes. Only a Summarized/APPROVED row counts as done — the old 120 s
// "force complete" is what auto-opened the previous visit's note.
const COMPLETION_FAST_MS = 4000;
const COMPLETION_FAST_WINDOW_MS = 120000;
const COMPLETION_SLOW_MS = 15000;
const COMPLETION_GIVE_UP_MS = 600000;

const RecordingContext = createContext(null);

export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error("useRecording must be used within a RecordingProvider");
  }
  return context;
};

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = (error) => reject(error);
  });
};

export const RecordingProvider = ({ children }) => {
  // === UI State ===
  const [isGlobalRecording, setIsGlobalRecording] = useState(false);
  const [isGlobalPaused, setIsGlobalPaused] = useState(false);
  const [globalElapsedTime, setGlobalElapsedTime] = useState(0);
  const [patientInfo, setPatientInfo] = useState(null);
  const [showFloatingPopup, setShowFloatingPopup] = useState(false);
  const [isOnRecordingPage, setIsOnRecordingPage] = useState(false);
  // The visit the live recording is filed to (classifyVisit of the chart row
  // it started on), for the "Recording for today's visit · 8:40 AM" line.
  const [recordingVisit, setRecordingVisit] = useState(null);
  // Set only after BOTH consent steps for a non-today visit; sent to
  // BlinkScribe as past_visit_confirmed=true (WebSocket and manual upload).
  const pastVisitConfirmedRef = useRef(false);
  // The department this recording is filed under: the SELECTED visit's own
  // (owner 10-09: record on that visit, its department) when it has one, else
  // the login's config department as before. Sent on the WebSocket and the
  // manual upload alike.
  const recordingDepartmentRef = useRef("");
  // BlinkScribe's refusal of this recording for its visit (safety net for the
  // owner rule), from a status/error message before the socket closes.
  const visitRefusalRef = useRef("");
  // The user already answered "another user's note on this visit" before the
  // start, so BlinkScribe's matching warning (code other_user_note) is noise.
  const otherNoteAnsweredRef = useRef(false);

  // === Live Scribe State (Passive Scribe — progressive SOAP via Gemini Live) ===
  const [liveSOAPNote, setLiveSOAPNote] = useState(null);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [suggestedICD10, setSuggestedICD10] = useState([]);
  const [suggestedOrders, setSuggestedOrders] = useState([]);
  // Order suggestions v2 (BlinkScribe order_suggest_v2, enabled providers only): the latest payload of the
  // recording socket, { ...payload } with its own appointmentid / patientid (the CareIQ tab shows it only
  // on that visit). null = this session has no v2 (everyone else): the tab keeps today's suggestions.
  const [orderSuggestionsV2, setOrderSuggestionsV2] = useState(null);
  // "Ordered by voice" (BlinkScribe order_suggest_v2/voice.py, Order Review providers only): the latest
  // websocket {type: "voice_orders"} of this recording, { appointmentid, patientid, rows: [{ concept,
  // order_name, order_type, quote, ts }], cancelled }. null = none heard / not enabled.
  const [voiceOrders, setVoiceOrders] = useState(null);
  // The live one-tap Orders card (BlinkScribe order_suggest_v2/live.py, owner 10-23): the latest websocket
  // {type: "live_orders"} of this recording, { enabled, appointmentid, patientid, rows: [{ concept, order_name,
  // variants, order_type, source, reason, first_ts }], placed, gone }. null = not enabled for this session.
  const [liveOrders, setLiveOrders] = useState(null);
  const [careIQAlerts, setCareIQAlerts] = useState([]);
  const [toolCallCount, setToolCallCount] = useState(0);
  const [liveScribeHealthy, setLiveScribeHealthy] = useState(true);
  const liveScribeActivityRef = useRef(null); // tracks last WS activity

  // Server-pushed recording status (resilience signals from backend).
  // Shape: { severity: "warning"|"error"|"info", code: string, message: string, ts: number }
  const [recordingStatus, setRecordingStatus] = useState(null);
  // 45-min "are you still recording?" prompt.
  const [showLongRecordingPrompt, setShowLongRecordingPrompt] = useState(false);
  const lastLongPromptAtRef = useRef(0); // ms timestamp of the last prompt firing

  // === Post-recording processing state ===
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState(""); // "Transcribing", "Generating Note", "Complete"
  const [processingPercent, setProcessingPercent] = useState(0);
  const [lastSOAPNote, setLastSOAPNote] = useState(null);
  const [lastTranscript, setLastTranscript] = useState("");
  const [lastNotePatientId, setLastNotePatientId] = useState(null);
  const processingTimerRef = useRef(null);
  // The finished note of the recording that just stopped: { id, patientId,
  // appointmentId }. Set ONLY by the completion poll once that recording's own
  // row (id newer than the start snapshot, same appointment) is Summarized or
  // APPROVED. The chart auto-opens the editor with this id and nothing else.
  const [lastCompletedRecording, setLastCompletedRecording] = useState(null);
  // Start-of-recording snapshot: { patientId, appointmentId, date, promise }
  // where promise resolves to { maxId } (or null if the lookup failed).
  const recordingSnapshotRef = useRef(null);
  // Completion poll handles. `gen` invalidates in-flight async ticks when a
  // newer recording starts/saves or the provider unmounts.
  const completionRef = useRef({ gen: 0, timer: null, finishTimer: null });

  const cancelCompletionPoll = useCallback(() => {
    const c = completionRef.current;
    c.gen += 1;
    if (c.timer) { clearTimeout(c.timer); c.timer = null; }
    if (c.finishTimer) { clearTimeout(c.finishTimer); c.finishTimer = null; }
  }, []);

  useEffect(() => () => {
    // no provider, no recording: nothing may stay locked (tests remount it)
    unlockRecordingTarget();
    cancelCompletionPoll();
    if (processingTimerRef.current) {
      clearInterval(processingTimerRef.current);
      processingTimerRef.current = null;
    }
  }, [cancelCompletionPoll]);

  // === Recording Infrastructure Refs (survive navigation) ===
  const wsRef = useRef(null);
  const recorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  // Re-entrancy guard: blocks overlapping startRecording() calls (repeated mic
  // clicks) which otherwise spawn duplicate WebSockets + AudioContexts that
  // collide on the worklet (AbortError) and cascade into a runaway loop.
  const startingRef = useRef(false);
  // No-audio backstop: last time a non-silent PCM chunk was seen. If the mic is
  // muted/dead/denied, the worklet still runs but every chunk is ~silence — we
  // warn the provider instead of silently capturing an empty recording.
  const lastAudioAtRef = useRef(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedRef = useRef(false);
  const isManualStopRef = useRef(false);
  const isManualRecordStartRef = useRef(false);
  const blockerRef = useRef(false);
  const combineStatusRef = useRef(false);
  const latestAppRef = useRef("");
  const streamNameRef = useRef("");
  const streamFilenameRef = useRef("");
  const userRef = useRef("");
  const patientDataRef = useRef(null);
  const pendingChunksRef = useRef([]);  // Fix 3: Buffer for audio chunks during reconnection
  const heartbeatRef = useRef(null);    // Fix 5: Heartbeat interval ref
  const wsQueryStringRef = useRef("");  // Store query string for reconnection
  const deviceChangeHandlerRef = useRef(null); // mic-change listener (forensics)
  const micLabelRef = useRef("");       // active mic label, once getUserMedia has resolved (forensics)

  // Manual recording callbacks (set by SideFrame's useAudioRecorder)
  const manualRecordRef = useRef(null);
  const stopManualRecordingRef = useRef(null);
  const pauseManualRecordingRef = useRef(null);
  const resumeManualRecordingRef = useRef(null);

  // Callback for poll trigger (SideFrame-specific)
  const setPollTriggerRef = useRef(null);

  // Toast callbacks (set by whoever mounts the component)
  const showToastRef = useRef(null);

  // === Prevent accidental tab/browser close while recording ===
  useEffect(() => {
    // Mirror recording state to a window flag so the auto-updater (and any
    // other non-React code) can avoid reloading the page mid-recording.
    window.__bkRecordingActive = !!isGlobalRecording;
    const handler = (e) => {
      if (isGlobalRecording) {
        e.preventDefault();
        e.returnValue = "A recording is in progress. If you leave, the recording may be lost.";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isGlobalRecording]);

  // Reloads/redirects asked for during a recording (location switch, expired
  // sign-in, ...) are queued in utils/deferredNavigation instead of ending the
  // recording. This reports what they wait for: the recording, its note
  // processing, and the chart (closing it is a natural boundary at which a
  // queued load runs, once nothing is busy). Sits below the window flag
  // effect above, which updates first in the same commit.
  useEffect(() => {
    setDeferredNavigationState({
      recording: !!isGlobalRecording,
      processing: !!isProcessing,
      chartOpen: !!isOnRecordingPage,
    });
  }, [isGlobalRecording, isProcessing, isOnRecordingPage]);

  // === Timer Functions ===
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now() - (globalElapsedTime || 0) * 1000;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const timePassed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setGlobalElapsedTime(timePassed);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  // Tracks whether the provider has used Pause at any point during this
  // recording. Used by the short-recording stop confirmation: if the
  // provider deliberately paused & resumed they're intentionally controlling
  // timing, so we don't second-guess a short stop.
  const usedPauseRef = useRef(false);

  // Tell the live-scribe server about a pause/resume so its mic-deficit check
  // leaves paused time out (see pauseControlFrame). Stop also pauses first.
  const sendPauseState = useCallback((paused) => {
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(pauseControlFrame(paused));
      }
    } catch (_) {}
  }, []);

  // === Core Recording Functions ===
  const togglePause = useCallback(() => {
    if (isGlobalPaused) {
      // Resume
      pausedRef.current = false;
      sendPauseState(false);
      // Restart the no-audio clock: the last sound predates the pause, so a
      // pause over 6s otherwise flashes "No audio detected" on resume.
      lastAudioAtRef.current = Date.now();
      if (isManualRecordStartRef.current && resumeManualRecordingRef.current) {
        resumeManualRecordingRef.current();
      }
      startTimeRef.current = Date.now() - globalElapsedTime * 1000;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        const timePassed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setGlobalElapsedTime(timePassed);
      }, 1000);
      setIsGlobalPaused(false);
    } else {
      // Pause
      pausedRef.current = true;
      usedPauseRef.current = true;
      sendPauseState(true);
      if (isManualRecordStartRef.current && pauseManualRecordingRef.current) {
        pauseManualRecordingRef.current();
      }
      stopTimer();
      setIsGlobalPaused(true);
    }
  }, [isGlobalPaused, globalElapsedTime, stopTimer, sendPauseState]);

  const stopRecording = useCallback(() => {
    isManualStopRef.current = true;
    isManualRecordStartRef.current = false;

    if (deviceChangeHandlerRef.current) {
      try {
        navigator.mediaDevices.removeEventListener("devicechange", deviceChangeHandlerRef.current);
      } catch (_) {}
      deviceChangeHandlerRef.current = null;
    }

    if (recorderRef.current) {
      recorderRef.current.stopRecording(() => {
        recorderRef.current = null;
      });
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, "Recording stopped by user");
    }

    // Clean up heartbeat and pending chunks
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    pendingChunksRef.current = [];

    stopTimer();
    setGlobalElapsedTime(0);
    setIsGlobalRecording(false);
    setIsGlobalPaused(false);
    setShowFloatingPopup(false);
    setPatientInfo(null);
    setRecordingVisit(null);
    // the chart's recording target is free again (utils/recordingTarget.js)
    unlockRecordingTarget();

    // Reset live scribe state (but NOT lastSOAP/lastTranscript)
    // Preserve suggestedICD10 and suggestedOrders so CareIQ tab keeps showing them
    setLiveSOAPNote(null);
    setLiveTranscript("");
    setCareIQAlerts([]);
    setToolCallCount(0);

    setTimeout(() => {
      eventBus.emit("refreshDashboard");
    }, 6000);
  }, [stopTimer]);

  const onSave = useCallback(() => {
    if (isManualRecordStartRef.current && stopManualRecordingRef.current) {
      stopManualRecordingRef.current();
    }

    if (showToastRef.current) {
      showToastRef.current("Recording Saved Successfully", "success");
    }

    // Preserve SOAP data before stopRecording clears it
    setLastSOAPNote((prev) => liveSOAPNote || prev);
    setLastTranscript((prev) => liveTranscript || prev);

    // Capture which patient this note belongs to BEFORE stopRecording clears patientInfo
    setLastNotePatientId(patientDataRef.current?.patientid || null);
    // Forget any earlier recording's finished id — only this recording's own
    // row (found by the poll below) may be auto-opened.
    setLastCompletedRecording(null);

    // Start processing indicator
    setIsProcessing(true);
    setProcessingStage("Transcribing");
    setProcessingPercent(10);

    stopRecording();

    // For offline / manual-recording flow the audio sits in IndexedDB and
    // only uploads on a window 'online' event or full app reload — neither
    // happens automatically when the user just clicks Save while still in
    // the same session. Kick the uploader directly so the backend recording
    // row gets created and the poll below has something to find.
    try {
      if (isManualRecordStartRef.current) {
        // Lazy import to avoid circular dependency at module load
        import("../offlinestreaming/uploadrecordings")
          .then(({ UploadindexdbRecording }) => UploadindexdbRecording())
          .catch(() => { /* non-fatal */ });
      }
    } catch (_) { /* non-fatal */ }

    // Simulated processing stages with timed progression
    // Stage 1: Transcribing (10% → 45%) over ~8s
    // We cap at 88 — leaves a clear visual gap so users know there's more
    // work pending, and the final ~12% is driven by the actual completion
    // poll below (or the safety timeout). The previous 95% cap led to
    // "stuck at 90/95%" reports because users couldn't tell whether the
    // bar was moving or hung.
    let pct = 10;
    if (processingTimerRef.current) clearInterval(processingTimerRef.current);
    processingTimerRef.current = setInterval(() => {
      pct += 5;
      if (pct >= 45) {
        setProcessingStage("Generating Note");
      }
      if (pct >= 85) {
        setProcessingStage("Finalizing");
      }
      if (pct >= 88) {
        pct = 88;
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      setProcessingPercent(pct);
    }, 1500);

    // Poll for THIS recording's finished note (stale-note fix 2026-09-28).
    // The old poll took the patient's latest row of the UTC day and called it
    // done when it was anything but 'Processing' — 'Submitted', or an earlier
    // same-day row on a second recording, ended processing early, and the
    // 120 s timeout forced "Complete" too; each of those auto-opened the
    // previous visit's note. Now: rows newer than the start snapshot, same
    // appointment, Summarized/APPROVED only, Chicago calendar day.
    cancelCompletionPoll();
    const gen = completionRef.current.gen;
    const isCurrent = () => completionRef.current.gen === gen;
    const stopProgressTimer = () => {
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    };
    const patientId = patientDataRef.current?.patientid;
    if (patientId) {
      const appointmentId = String(patientDataRef.current?.appointmentid || "").trim();
      const snap = recordingSnapshotRef.current;
      const snapMatches = snap && String(snap.patientId) === String(patientId);
      const snapshotPromise = snapMatches ? snap.promise : Promise.resolve(null);
      const startDate = (snapMatches && snap.date) || localDateStr();
      const t0 = Date.now();
      let snapshot; // undefined = not resolved yet, null = unavailable

      const tick = async () => {
        if (!isCurrent()) return;
        if (snapshot === undefined) {
          try { snapshot = await snapshotPromise; } catch (_) { snapshot = null; }
          if (!isCurrent()) return;
        }
        try {
          // Per-request timeout: a hung request must not stall the chain
          // (the old setInterval kept ticking regardless).
          const resp = await blinkscribeapi.post(
            `/v1/patient/${patientId}/recording/details`,
            { filter_array: { date_filter: { start_date: startDate, end_date: localDateStr() } } },
            { timeout: 20000 }
          );
          if (!isCurrent()) return;
          const row = pickCompletedRow(rowsOf(resp), {
            maxId: snapshot ? snapshot.maxId : null,
            appointmentId,
          });
          if (row) {
            completionRef.current.timer = null;
            stopProgressTimer();
            setProcessingPercent(100);
            setProcessingStage("Complete");
            // Without a start snapshot we can't prove the row is this
            // recording's, so the processing UI ends but nothing auto-opens.
            if (snapshot) {
              setLastCompletedRecording({
                id: row.id,
                patientId: String(patientId),
                appointmentId: rowAppointmentId(row) || appointmentId,
              });
            }
            // Keep isProcessing true for a moment so UI shows 100%
            completionRef.current.finishTimer = setTimeout(() => {
              completionRef.current.finishTimer = null;
              if (!isCurrent()) return;
              setIsProcessing(false);
              eventBus.emit("refreshDashboard");
            }, 1500);
            return;
          }
        } catch (e) {
          // Polling error — non-blocking
        }
        if (!isCurrent()) return;
        const elapsed = Date.now() - t0;
        if (elapsed >= COMPLETION_GIVE_UP_MS) {
          // Give up WITHOUT declaring success and without auto-opening
          // anything; the note shows up in the chart once it's finished.
          completionRef.current.timer = null;
          stopProgressTimer();
          setProcessingStage("Still processing");
          if (showToastRef.current) {
            showToastRef.current("The note is still processing — it will appear in the chart when it's ready", "info");
          }
          setIsProcessing(false);
          eventBus.emit("refreshDashboard");
          return;
        }
        if (elapsed >= COMPLETION_FAST_WINDOW_MS) setProcessingStage("Still finalizing");
        completionRef.current.timer = setTimeout(
          tick,
          elapsed < COMPLETION_FAST_WINDOW_MS ? COMPLETION_FAST_MS : COMPLETION_SLOW_MS
        );
      };
      completionRef.current.timer = setTimeout(tick, COMPLETION_FAST_MS);
    } else {
      // No patient ID — just run the simulated timer to completion
      completionRef.current.timer = setTimeout(() => {
        completionRef.current.timer = null;
        if (!isCurrent()) return;
        stopProgressTimer();
        setProcessingPercent(100);
        setProcessingStage("Complete");
        completionRef.current.finishTimer = setTimeout(() => {
          completionRef.current.finishTimer = null;
          if (isCurrent()) setIsProcessing(false);
        }, 1500);
      }, 30000);
    }

    if (setPollTriggerRef.current) {
      setTimeout(() => {
        setPollTriggerRef.current((prev) => prev + 1);
      }, 8000);
    }
  }, [stopRecording, liveSOAPNote, liveTranscript, cancelCompletionPoll]);

  const onDelete = useCallback(async () => {
    try {
      const response = await blinkscribeapi.post(
        `/v1/recording/streaming/cancel?stream_filename=${streamNameRef.current}`
      );
      if (response.status === 200) {
        if (isManualRecordStartRef.current) {
          blockerRef.current = true;
          if (stopManualRecordingRef.current) {
            stopManualRecordingRef.current();
          }
        }
        stopRecording();

        if (showToastRef.current) {
          showToastRef.current("Recording Cancelled Successfully", "info");
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      blockerRef.current = false;
    }
  }, [stopRecording]);

  // opts.selected: `data` is the visit the provider SELECTED on the chart
  // (utils/recordingTarget.js), not the chart's live visit today: today's
  // visit that did not happen is confirmed too (with a warning), and the
  // recording is filed under that visit's department.
  const startRecording = useCallback(async (data, token, combineStatus, latestApp, opts = {}) => {
    const selectedVisit = Boolean(opts && opts.selected);
    // Guard against overlapping starts. A start already in flight is always
    // blocked. An existing recorderRef is only a real session if its WebSocket
    // is still OPEN — otherwise it's a stale/broken session (e.g. the tab was
    // backgrounded, the WS dropped, or a prior start half-failed) and blocking
    // on it would dead-lock the mic until a full page refresh. In that case we
    // tear the corpse down and start fresh so the button always works.
    if (startingRef.current) {
      console.warn("[LiveScribe] startRecording ignored — a start is already in flight");
      return { success: false, error: "already-starting" };
    }
    if (recorderRef.current) {
      const wsOpen = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
      if (wsOpen) {
        console.warn("[LiveScribe] startRecording ignored — a session is already live");
        if (showToastRef.current) {
          showToastRef.current("A recording is already in progress for this visit", "info");
        }
        return { success: false, error: "already-recording" };
      }
      console.warn("[LiveScribe] clearing a stale/broken session before starting a new one");
      try { recorderRef.current.stopRecording && recorderRef.current.stopRecording(); } catch (e) {}
      recorderRef.current = null;
      try { wsRef.current && wsRef.current.close(); } catch (e) {}
      wsRef.current = null;
    }
    startingRef.current = true;

    pausedRef.current = false;
    isManualStopRef.current = false;
    isManualRecordStartRef.current = false;
    usedPauseRef.current = false;
    micLabelRef.current = "";

    // Hoisted so the catch can tear down a partially-started session.
    let ws = null;
    let audioContext = null;

    try {
      // Visit gate (owner rule 2026-09-29). Runs before ANY side effect of a
      // start: /auth/me, the snapshot, the orders/confirm status ping, the
      // Athena check-in (vitals/prepare), the WebSocket and the microphone.
      // Today's visit passes at once (no call, no dialog). Any other visit
      // is blocked when the patient has a visit today, and otherwise needs
      // the two-step consent. Every chart start (mic buttons, recording
      // nudge) comes through here.
      let visitGate = await guardRecordingVisit(data, { confirmNotHeld: selectedVisit });
      if (!visitGate.allowed) {
        console.warn(`[LiveScribe] recording not started: ${visitGate.reason}`);
        return {
          success: false,
          error: visitGate.reason === "today-exists" ? "visit-today-exists" : "visit-consent-declined",
        };
      }
      pastVisitConfirmedRef.current = Boolean(visitGate.pastVisitConfirmed);
      visitRefusalRef.current = "";

      // Another user's note on this visit today (BlinkScribe 10-06): it is no
      // longer merged into silently. Ask: add to it (the explicit combine) or
      // keep a separate note. Read-only lookup; any failure asks nothing.
      // When that note is SIGNED (patient 4662), the question offers the
      // user's own visit today instead.
      otherNoteAnsweredRef.current = false;
      if (!combineStatus) {
        let pick = await chooseCombineTarget(data?.appointmentid);
        if (pick.switchTo) {
          // Record on the user's own visit: the dashboard re-points the open
          // chart to it through openChart (the path a card click takes), and
          // this recording continues on that card.
          const card = await requestChartVisit({
            patientid: data?.patientid,
            appointmentid: pick.switchTo.appointmentid,
          });
          if (!card) {
            console.warn("[LiveScribe] recording not started: could not open the chosen visit");
            if (showToastRef.current) {
              showToastRef.current(
                "Could not open that visit. Open it from the dashboard and start recording there.",
                "warning"
              );
            }
            return { success: false, error: "switch-visit-failed" };
          }
          data = card;
          visitGate = await guardRecordingVisit(data, { confirmNotHeld: selectedVisit });
          if (!visitGate.allowed) {
            console.warn(`[LiveScribe] recording not started after the visit switch: ${visitGate.reason}`);
            return {
              success: false,
              error: visitGate.reason === "today-exists" ? "visit-today-exists" : "visit-consent-declined",
            };
          }
          pastVisitConfirmedRef.current = Boolean(visitGate.pastVisitConfirmed);
          // The new visit gets the same check (another user's note there);
          // no second switch is offered.
          pick = await chooseCombineTarget(data?.appointmentid, { allowSwitch: false });
        }
        if (pick.cancelled) {
          console.warn("[LiveScribe] recording not started: another user's note on this visit, cancelled");
          return { success: false, error: "combine-choice-cancelled" };
        }
        otherNoteAnsweredRef.current = pick.answered;
        if (pick.combineInto) {
          combineStatus = true;
          latestApp = pick.combineInto;
        }
      }
      setRecordingVisit(visitGate.info);
      // From here the chart's recording target is this visit until the
      // recording stops (a visit selected meanwhile is refused).
      lockRecordingTarget(data, visitGate.info, { selected: selectedVisit });
      // Today's visit (held or not): the visit-status ping and the Athena
      // check-in below are for today's visit only, never a past / future one.
      const recordingToday = visitGate.info?.kind === "today";

      console.time("⏱ [3] startRecording TOTAL");
      console.time("⏱ [3a] /auth/me");
      const response = await blinkscribeapi.get("/auth/me");
      console.timeEnd("⏱ [3a] /auth/me");
      userRef.current = response;
      patientDataRef.current = data;

      const filenamee = crypto.randomUUID();
      streamNameRef.current = filenamee;

      const notes = "Sample notes";
      const tags = "Sample tags";
      let patientname = `${data?.firstname} ${data?.lastname}`;
      const practiseid = response?.config?.practiceid;
      let departmentid = response?.config?.departmentid;
      const visitDept = Number(data?.departmentid);
      if (selectedVisit && Number.isInteger(visitDept) && visitDept > 0) {
        departmentid = String(visitDept);
      }
      recordingDepartmentRef.current = departmentid;
      const appointmentid = String(data?.appointmentid || "").trim();

      // Completion snapshot: the newest recording id that already exists for
      // this patient today, taken BEFORE this recording can create a row.
      // onSave's poll only accepts a finished row newer than this, so a
      // same-day earlier row (combine visits) or a previous visit's note can
      // never be mistaken for this recording's note.
      if (data?.patientid) {
        const snapDate = localDateStr();
        recordingSnapshotRef.current = {
          patientId: String(data.patientid),
          appointmentId: appointmentid,
          date: snapDate,
          promise: blinkscribeapi.post(
            `/v1/patient/${data.patientid}/recording/details`,
            { filter_array: { date_filter: { start_date: snapDate, end_date: snapDate } } },
            { timeout: 20000 }
          ).then((resp) => ({ maxId: snapshotMaxId(rowsOf(resp)) }))
           .catch(() => null),
        };
      } else {
        recordingSnapshotRef.current = null;
      }

      // 🔥 Pre-warm patient context in Redis (fire-and-forget)
      // This gives the backend a head start to cache patient history
      // before the LiveScribe WebSocket session starts.
      try {
        blinkscribeapi.post("/v1/recording/pre-warm", {
          practiceid: String(practiseid || ""),   // endpoint validates as string (was 422 on int)
          patientid: String(data?.patientid || ""),
          departmentid: String(departmentid || ""),
        }).then((res) => {
          console.log("[LiveScribe] Pre-warm response:", res?.data);
        }).catch((err) => {
          console.warn("[LiveScribe] Pre-warm failed (non-blocking):", err?.message);
        });
        console.log("[LiveScribe] Pre-warm request fired for patient:", data?.patientid);
      } catch (e) {
        console.warn("[LiveScribe] Pre-warm skipped:", e?.message);
      }

      combineStatusRef.current = combineStatus;
      latestAppRef.current = latestApp;

      // Update visit status to "Processing" when recording starts
      // (fire-and-forget). Via the v2 axios client: the old relative fetch
      // silently hit the SPA fallback after the 08-20 single-domain cutover
      // (the unified vhost proxies nothing), so this ping had been dead.
      if (recordingToday && data?.patientid && appointmentid) {
        careiqapi.post(`/v1/${practiseid}/patient/${data.patientid}/orders/confirm`, {
          // Status ping only — never send diagnosis/lov/tov here: the
          // confirm endpoint used to write them wholesale, so this ping
          // wiped the appointment's saved diagnosis on every recording.
          patient_name: patientname, createdby: response?.username || "",
          orderdate: new Date().toISOString().split("T")[0],
          appointmentid: parseInt(appointmentid) || 0,
          token_no: 0, confirm_status: "E", update_status: "C",
        }).catch(() => {});
      }

      // Real Athena check-in (fire-and-forget). The status ping above is
      // local-only and order-gated — it never checks the patient in on
      // Athena, so a walk-in whose visit starts at the recorder had no
      // encounter for vitals or note posting (Gagan, QA 2026-08-26). The
      // prepare endpoint answers from appointment_master when an encounterid
      // is already stored (zero Athena calls), otherwise checks the patient
      // in and waits briefly for the encounter Athena mints.
      if (recordingToday && data?.patientid) {
        api.post(`/v1/${practiseid}/intake/patient/${data.patientid}/vitals/prepare`, {}).catch(() => {});
      }

      let filenameobj = {
        practiceid: practiseid,
        departmentid: departmentid,
        patientid: data?.patientid,
        currentUser: "PRO",
        fileExtension: "txt",
        appointmentid: appointmentid,
      };

      // A combine keeps a FRESH file name, as BlinkScribe's auto-combine has
      // since 08-17: the parent-derived "<parent>_combine_edit" name collides
      // when two recordings are added to one note before the first finishes.
      // The combine itself travels in category/rowid/filename (below).

      const generatedfilename = generateFilename(filenameobj);

      // template_name=HnP: the pre-LiveScribe chart mic (sideframe streaming)
      // hardcoded the H&P template for every recording, which is what put
      // Social/Family/Medical/Surgical History and Allergies in the note and
      // in the Athena post. The LiveScribe switch dropped it to the default
      // SOAP schema and those sections silently vanished (QA 2026-08-26).
      let queryString = `token=${token}&stream_filename=${filenamee}&generated_file_name=${generatedfilename}&practiceid=${practiseid}&departmentid=${departmentid}&patientid=${data?.patientid}&appointmentid=${appointmentid}&template_name=HnP&notes=${encodeURIComponent(notes)}&tags=${encodeURIComponent(tags)}&patientname=${encodeURIComponent(patientname)}`;
      // Which client this is, and - only after both consent steps for a visit
      // that is not today's - that the provider confirmed it. BlinkScribe
      // refuses a non-today visit without it.
      queryString += "&client=care";
      if (pastVisitConfirmedRef.current) {
        queryString += "&past_visit_confirmed=true";
      }

      if (combineStatus) {
        streamFilenameRef.current = latestApp?.audiolink;
        queryString += `&category=combine&filename=${latestApp?.audiolink}&rowid=${latestApp?.id}`;
      }

      // Fix 3: Auto-reconnect WebSocket with exponential backoff
      const MAX_RETRIES = 5;
      const RETRY_DELAY_MS = 2000;

      // The container build serves the UI and the API from one host, so a
      // relative WS path works there. hub.preview does not: it is a static
      // vhost with no /v2/bs proxy, so window.location.host resolved to
      // hub.preview.brainymed.com and every connect failed with 1006. Derive
      // the socket host from the BlinkScribe client instead, so it follows
      // whatever api/blinkscribeapi.js points at.
      const connectWebSocket = (qs, retryCount = 0) => {
        let wsOrigin;
        try {
          const base = blinkscribeapi?.defaults?.baseURL || "";
          const apiUrl = new URL(base, window.location.origin);
          wsOrigin = `${apiUrl.protocol === "https:" ? "wss" : "ws"}://${apiUrl.host}`;
        } catch (e) {
          wsOrigin = `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;
        }
        // The first connect races getUserMedia, so the mic label is usually
        // not known yet; reconnects carry it (onopen covers the first one).
        const labelAtConnect = micLabelRef.current;
        const socket = new WebSocket(
          `${wsOrigin}/v2/bs/v1/recording/live-scribe?${qs}${micLabelQueryParam(labelAtConnect)}`
        );

        socket.onopen = () => {
          // Finished connecting after its session was stopped or replaced by a
          // new recording: close it, and never flush the (shared) pending
          // audio into it -- that is the next patient's audio.
          if (isManualStopRef.current || wsRef.current !== socket) {
            console.warn("[LiveScribe] closing a socket whose session already ended");
            try { socket.close(1000, "Recording session ended"); } catch (_) {}
            return;
          }
          console.log(`[LiveScribe] WebSocket connected${retryCount > 0 ? ` (reconnect #${retryCount})` : ""}`);
          setLiveScribeHealthy(true);
          liveScribeActivityRef.current = Date.now();
          // Flush any buffered chunks
          while (pendingChunksRef.current.length > 0) {
            const chunk = pendingChunksRef.current.shift();
            socket.send(chunk);
          }
          // Mic label learned after this socket's URL was built (getUserMedia
          // resolved while it was still connecting): send it now, otherwise
          // the server logs 'mic: unknown' for the whole session.
          if (micLabelRef.current && micLabelRef.current !== labelAtConnect) {
            socket.send(JSON.stringify({ type: "miclabel", label: micLabelRef.current }));
          }
          // A reconnect is a fresh server session: re-announce an active pause.
          if (pausedRef.current) {
            socket.send(pauseControlFrame(true));
          }
          // Fix 5: Start heartbeat
          if (heartbeatRef.current) clearInterval(heartbeatRef.current);
          heartbeatRef.current = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ type: "heartbeat" }));
            }
          }, 15000);
        };

        // Handle incoming events from Gemini Live backend
        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // --- BlinkScribe refused this recording ---
            // (past visit while the patient has one today, no consent, or the
            // recording belongs to another practice than this session's).
            // Remembered so the close that follows stops the session instead
            // of reconnecting or falling back to a local recording.
            if (
              data &&
              (data.type === "error" || data.type === "recording_status") &&
              isVisitRefusal({ reason: data.code })
            ) {
              visitRefusalRef.current = refusalMessage(
                { reason: data.code, message: data.message },
                "BlinkScribe refused a recording on this visit."
              );
            }

            // --- Session resumed after server restart ---
            if (data.type === "session_resumed") {
              console.log(`[LiveScribe] Session resumed with ${data.transcript_length} chars of prior transcript`);
              if (showToastRef.current) {
                showToastRef.current("Recording reconnected — context restored", "info");
              }
              return;
            }

            // --- Main SOAP update (log_encounter_data tool) ---
            if (data.type === "soap_update") {
              console.log(`[LiveScribe] SOAP update #${data.tool_call_number}`, data);
              liveScribeActivityRef.current = Date.now();
              setLiveScribeHealthy(true);
              if (data.soap_note) setLiveSOAPNote(data.soap_note);
              if (data.transcript_segment) {
                setLiveTranscript((prev) => prev + data.transcript_segment + "\n");
              }
              if (data.suggested_icd10) setSuggestedICD10(data.suggested_icd10);
              if (data.suggested_orders) setSuggestedOrders(data.suggested_orders);
              if (data.careiq_alerts?.length > 0) {
                setCareIQAlerts((prev) => [...prev, ...data.careiq_alerts]);
              }
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeUpdate", data);
            }

            // --- Order suggestions v2 (enabled providers only; announce + every pass) ---
            if (data.type === "order_suggestions_v2") {
              const { type: _t, ...payload } = data;
              setOrderSuggestionsV2(payload);
              eventBus.emit("liveScribeOrderSuggestionsV2", payload);
              return;
            }

            // --- "Ordered by voice": the provider's spoken order commands (rows, full list each time) ---
            if (data.type === "voice_orders") {
              const { type: _vt, ...vo } = data;
              setVoiceOrders(vo);
              eventBus.emit("liveScribeVoiceOrders", vo);
              return;
            }

            // --- The live Orders card: the full row list each time it changes ---
            if (data.type === "live_orders") {
              const { type: _lt, ...lo } = data;
              setLiveOrders(lo);
              eventBus.emit("liveScribeLiveOrders", lo);
              return;
            }

            // --- Granular tool: suggest_diagnosis ---
            if (data.type === "suggest_diagnosis") {
              console.log(`[LiveScribe] 🏥 Suggested Dx: ${data.icd10_code} ${data.description}`);
              setSuggestedICD10((prev) => {
                const entry = data.icd10_code
                  ? `${data.icd10_code} - ${data.description}`
                  : data.description;
                return prev.includes(entry) ? prev : [...prev, entry];
              });
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeDiagnosis", data);
            }

            // --- Granular tool: set_level_of_visit ---
            if (data.type === "set_level_of_visit") {
              console.log(`[LiveScribe] 📊 LoV: ${data.level} (${data.reasoning})`);
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeLoV", data);
            }

            // --- Granular tool: add_suggested_order ---
            if (data.type === "add_suggested_order") {
              console.log(`[LiveScribe] 📋 Order: ${data.order_name} (${data.order_type})`);
              setSuggestedOrders((prev) => {
                // prev may hold full order objects (from soap_update) or strings
                // (legacy). Dedupe by order_name; keep full structured shape.
                const seen = new Set(prev.map((o) =>
                  typeof o === "string" ? o : (o?.order_name || o?.order || "")
                ).filter(Boolean));
                if (seen.has(data.order_name)) return prev;
                return [...prev, {
                  order_name: data.order_name,
                  order_type: data.order_type,
                  details: data.details,
                }];
              });
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeOrder", data);
            }

            // --- Recording resilience signals from server ---
            // Backend sends these when something is degraded so the provider
            // sees a banner immediately instead of finding out at end-of-session.
            // other_user_note repeats the question answered before the start.
            if (data.type === "recording_status" && data.code === "other_user_note" && otherNoteAnsweredRef.current) {
              return;
            }
            if (data.type === "recording_status") {
              console.warn(`[LiveScribe] status ${data.severity}/${data.code}: ${data.message}`);
              if (data.severity === "info") {
                // info messages clear any active warning (e.g., transcript_recovered)
                setRecordingStatus(null);
              } else {
                setRecordingStatus({
                  severity: data.severity,
                  code: data.code,
                  message: data.message,
                  ts: Date.now(),
                });
                if (showToastRef.current) {
                  showToastRef.current(data.message, data.severity === "error" ? "error" : "warning");
                }
              }
              eventBus.emit("liveScribeStatus", data);
            }
          } catch (e) {
            // Not JSON — ignore (could be binary or other)
          }
        };

        socket.onerror = (error) => {
          console.error("WebSocket error:", error);
        };

        socket.onclose = (event) => {
          // A socket that is no longer the session's (stopped + restarted, or
          // replaced) must not touch the current session's heartbeat,
          // reconnect or manual-fallback state.
          if (wsRef.current !== socket) return;
          // Clear heartbeat on close
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
          }

          // Refused for its visit or practice (close 4409 / 4428 / 4403): no
          // reconnect (it would be refused again) and no local fallback
          // recording (its upload would be refused, or worse, filed). Stop the
          // session and say why.
          if (
            !isManualStopRef.current &&
            (visitRefusalRef.current || isVisitRefusal({ code: event.code, reason: event.reason }))
          ) {
            const why =
              visitRefusalRef.current ||
              refusalMessage(
                { code: event.code, reason: event.reason },
                "BlinkScribe refused a recording on this visit."
              );
            visitRefusalRef.current = "";
            console.warn(`[LiveScribe] recording refused for its visit (code ${event.code}): ${why}`);
            stopRecording();
            if (showToastRef.current) {
              showToastRef.current(`Recording stopped: ${why}`, "error");
            }
            return;
          }

          if (!isManualStopRef.current && retryCount < MAX_RETRIES) {
            const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
            console.warn(`WebSocket closed (code ${event.code}). Reconnecting in ${delay}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
            setTimeout(() => {
              // Stopped (Save/Discard) or a new recording started during the
              // backoff: reconnecting would reopen the old stream and take
              // over wsRef, sending the next patient's audio to it.
              if (isManualStopRef.current || wsRef.current !== socket) return;
              const newSocket = connectWebSocket(qs, retryCount + 1);
              wsRef.current = newSocket;
            }, delay);
          } else if (!isManualStopRef.current && retryCount >= MAX_RETRIES) {
            console.warn("Max WebSocket retries reached. Falling back to manual recording.");
            setLiveScribeHealthy(false);
            isManualRecordStartRef.current = true;
            if (manualRecordRef.current) manualRecordRef.current();
          } else {
            console.log("WebSocket connection closed.");
          }
        };

        return socket;
      };

      wsQueryStringRef.current = queryString;
      console.time("⏱ [3b] WebSocket connect (non-blocking)");
      ws = connectWebSocket(queryString);
      wsRef.current = ws;
      console.timeEnd("⏱ [3b] WebSocket connect (non-blocking)");

      // BlinkScribe can refuse the recording for its visit while the
      // microphone is still opening (the socket connects in parallel); the
      // refusal stops the session (isManualStop). Then nothing may start:
      // release the mic and bail out through the catch below.
      const abortIfStoppedDuringStart = () => {
        if (!isManualStopRef.current) return;
        try {
          audioStreamRef.current?.getTracks().forEach((track) => track.stop());
        } catch (_) {}
        audioStreamRef.current = null;
        throw new Error("recording stopped while starting");
      };

      console.time("⏱ [3c] getUserMedia");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      console.timeEnd("⏱ [3c] getUserMedia");
      abortIfStoppedDuringStart();
      // Mic forensics (patient 8512, 2026-08-28: a device switch mid-visit
      // silently dropped a third of the audio) — log the active mic and
      // toast if the device set changes while recording.
      const micLabel = stream.getAudioTracks?.()[0]?.label || "unknown";
      micLabelRef.current = micLabel;
      console.log(`[LiveScribe] Microphone: ${micLabel}`);
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "miclabel", label: micLabel }));
        }
      } catch (_) {}
      const onDeviceChange = () => {
        const lbl = audioStreamRef.current?.getAudioTracks?.()[0]?.label || "unknown";
        console.warn(`[LiveScribe] Audio devices changed; recording mic: ${lbl}`);
        showToastRef.current && showToastRef.current(`Audio device changed — still recording on: ${lbl}`, "info");
      };
      try {
        navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
        deviceChangeHandlerRef.current = onDeviceChange;
      } catch (_) {}

      console.time("⏱ [3d] AudioWorklet load");
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      // The click gesture has already been spent by the awaits above (/auth/me,
      // getUserMedia), so Chrome's autoplay policy can leave the context
      // "suspended". A suspended context never pulls audio through the worklet —
      // every frame is silence and the server hears nothing. Force-resume it.
      if (audioContext.state === "suspended") {
        try {
          await audioContext.resume();
        } catch (e) {
          console.warn("[LiveScribe] audioContext.resume() failed", e);
        }
      }
      // Cache-bust so a stale 404 (from before the file existed) can't stick.
      await audioContext.audioWorklet.addModule("/pcm-processor.js?v=2");
      console.timeEnd("⏱ [3d] AudioWorklet load");
      abortIfStoppedDuringStart();

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, "pcm-processor");

      // Downsample from browser's native rate to 16kHz
      const downsample = (buffer, fromRate, toRate) => {
        if (fromRate === toRate) return buffer;
        const ratio = fromRate / toRate;
        const newLength = Math.round(buffer.length / ratio);
        const result = new Float32Array(newLength);
        for (let i = 0; i < newLength; i++) {
          result[i] = buffer[Math.floor(i * ratio)];
        }
        return result;
      };

      // Convert Float32 PCM → Int16 PCM ArrayBuffer
      const toInt16 = (float32) => {
        const int16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          int16[i] = Math.max(-32768, Math.min(32767, Math.round(float32[i] * 32767)));
        }
        return int16.buffer;
      };

      // Adaptive input-rate compensation (2026-08-28): some audio stacks
      // (Intel Smart Sound on Dr. Adupa's machine) deliver samples at a
      // fraction of the context's claimed rate, so recordings come out
      // time-compressed (1.5x chipmunk audio, ~35% of speech effectively
      // lost to transcription). Measure the real delivery rate against the
      // wall clock; on a stable partial deficit, feed the TRUE device rate
      // into downsample() so the output is normal-speed 16kHz.
      const rateWindow = createRateWindow(performance.now());
      let rateFactor = 1;            // multiplier on the claimed context rate
      let rateFactorAnnounced = false;

      workletNode.port.onmessage = (event) => {
        if (pausedRef.current) {
          // Nothing is captured while paused: keep restarting the rate window
          // so no window spans a pause (it read as a false frame-drop deficit).
          rateWindow.reset(performance.now());
          return;
        }
        // Track real audio: peak amplitude of this Float32 chunk. Speech peaks
        // well above 0.01; steady silence stays ~0. Feeds the no-audio backstop.
        let peak = 0;
        for (let i = 0; i < event.data.length; i++) {
          const a = event.data[i] < 0 ? -event.data[i] : event.data[i];
          if (a > peak) peak = a;
        }
        if (peak > 0.01) lastAudioAtRef.current = Date.now();

        const r = rateWindow.add(event.data.length, audioContext.sampleRate, performance.now());
        if (r !== null) {
          // 0.45–0.92 = a device rate lie; ≥0.92 = healthy; below 0.45 the
          // window likely spanned a pause/tab-suspend — keep current factor.
          // MEASUREMENT ONLY (2026-08-28): resampling by r was WRONG — the
          // stored audio turned out to be normal-pitch, so the deficit is
          // dropped frames, not a rate lie; "compensating" made recordings
          // deep and slow. Keep the visibility, never alter the audio.
          if (r > 0.45 && r < 0.92 && !rateFactorAnnounced) {
            rateFactorAnnounced = true;
            console.warn(`[LiveScribe] Mic delivering ${Math.round(r * 100)}% of real-time samples (frame drops)`);
            try {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "miclabel", label: `frame-drop deficit ${Math.round(r * 100)}%` }));
              }
            } catch (_) {}
          }
          rateFactor = 1;
        }

        const downsampled = downsample(event.data, audioContext.sampleRate * rateFactor, 16000);
        const pcm16Buffer = toInt16(downsampled);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(pcm16Buffer);
        } else {
          // Buffer PCM chunks (as ArrayBuffer) during reconnect
          pendingChunksRef.current.push(pcm16Buffer);
          if (pendingChunksRef.current.length > 60) {
            pendingChunksRef.current.shift();
            console.warn("Pending PCM buffer exceeded 60 chunks — dropping oldest");
          }
        }
      };

      source.connect(workletNode);
      // Mute worklet output (avoid feedback)
      const muteGain = audioContext.createGain();
      muteGain.gain.value = 0;
      workletNode.connect(muteGain);
      muteGain.connect(audioContext.destination);

      // Store worklet in recorderRef so stopRecording() can clean it up
      recorderRef.current = {
        audioContext,
        workletNode,
        source,
        stopRecording: (cb) => {
          workletNode.disconnect();
          source.disconnect();
          audioContext.close();
          if (cb) cb();
        },
      };


      setGlobalElapsedTime(0);
      startTimeRef.current = Date.now();
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        const timePassed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setGlobalElapsedTime(timePassed);
      }, 1000);

      // A previous recording's completion poll must not end THIS recording's
      // processing early or auto-open its note into this session.
      cancelCompletionPoll();
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      setIsProcessing(false);
      setProcessingStage("");
      setProcessingPercent(0);
      setLastCompletedRecording(null);

      setIsGlobalRecording(true);
      setVoiceOrders(null);
      setLiveOrders(null);
      setIsGlobalPaused(false);
      setLiveScribeHealthy(true);
      // NB: we deliberately do NOT force showFloatingPopup here. On the patient
      // chart the sideframe's own in-chart recorder is the indicator; the
      // floating pop-up is only meant for when the provider navigates away from
      // the chart while a session keeps running. The no-audio safety banner is
      // decoupled from this flag (see FloatingRecorder) so it still shows here.

      // Monitor LiveScribe activity — mark unhealthy if no WS messages for 90s
      liveScribeActivityRef.current = Date.now();
      const liveCheckInterval = setInterval(() => {
        if (!isManualStopRef.current && liveScribeActivityRef.current) {
          const elapsed = Date.now() - liveScribeActivityRef.current;
          if (elapsed > 90000 && wsRef.current?.readyState !== WebSocket.OPEN) {
            setLiveScribeHealthy(false);
          }
        }
      }, 15000);

      // No-audio backstop — if no non-silent audio for NO_AUDIO_MS, warn the
      // provider loudly (banner + toast) so a mic that's muted/denied/dead can
      // never masquerade as a working recording. Clears itself if audio resumes.
      lastAudioAtRef.current = Date.now();
      const NO_AUDIO_MS = 6000;
      let noAudioWarned = false;
      const audioSilenceCheck = setInterval(() => {
        if (isManualStopRef.current || pausedRef.current) return;
        const silentFor = Date.now() - lastAudioAtRef.current;
        if (silentFor > NO_AUDIO_MS && !noAudioWarned) {
          noAudioWarned = true;
          setRecordingStatus({
            severity: "error", code: "no_audio",
            message: "No audio detected — check your microphone. This recording is capturing silence.",
            ts: Date.now(),
          });
          if (showToastRef.current) {
            showToastRef.current("No audio detected — check your microphone", "error");
          }
        } else if (silentFor <= NO_AUDIO_MS && noAudioWarned) {
          noAudioWarned = false;
          setRecordingStatus(null);
        }
      }, 2000);

      // Long-recording guard — every 45 min of continuous recording, prompt
      // the provider to confirm they're still recording. Prevents runaway
      // recordings if a session was forgotten / left running accidentally.
      lastLongPromptAtRef.current = Date.now();
      const LONG_RECORDING_INTERVAL_MS = 45 * 60 * 1000;
      const longRecordingCheckInterval = setInterval(() => {
        if (isManualStopRef.current) return;
        const now = Date.now();
        if (now - lastLongPromptAtRef.current >= LONG_RECORDING_INTERVAL_MS) {
          lastLongPromptAtRef.current = now;
          setShowLongRecordingPrompt(true);
        }
      }, 60000); // poll every minute

      // Clean up on stop
      const origStop = recorderRef.current?.stopRecording;
      if (recorderRef.current) {
        const wrapped = (cb) => {
          clearInterval(liveCheckInterval);
          clearInterval(longRecordingCheckInterval);
          clearInterval(audioSilenceCheck);
          setShowLongRecordingPrompt(false);
          setRecordingStatus(null);
          origStop?.(cb);
        };
        recorderRef.current.stopRecording = wrapped;
      }
      setPatientInfo({
        firstname: data?.firstname,
        lastname: data?.lastname,
        patientid: data?.patientid,
      });
      console.timeEnd("⏱ [3] startRecording TOTAL");

      // visit: the card this recording is on (another visit than the
      // caller's when the user switched to their own visit).
      return { success: true, streamfilename: filenamee, visit: data };
    } catch (error) {
      console.error("Error starting recording:", error);
      // Tear down anything partially started so a failed attempt can't leave an
      // orphaned WebSocket (heartbeat + auto-reconnect) or AudioContext behind —
      // that accumulation was the runaway. isManualStop stops the WS from
      // auto-reconnecting on the close we trigger here.
      isManualStopRef.current = true;
      try { ws?.close(); } catch (e) {}
      wsRef.current = null;
      try { await audioContext?.close(); } catch (e) {}
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      recorderRef.current = null;
      setIsGlobalRecording(false);
      setRecordingVisit(null);
      unlockRecordingTarget();
      return { success: false, error };
    } finally {
      startingRef.current = false;
    }
  }, [cancelCompletionPoll, stopRecording]);

  const submitAudioBlobManually = useCallback((blob) => {
    if (blob && blob.type.startsWith("audio/")) {
      if (blockerRef.current) return;

      const data = patientDataRef.current;
      const user = userRef.current;

      let obj = {
        file: blob,
        practiceid: user?.config?.practiceid,
        departmentid: recordingDepartmentRef.current || user?.config?.departmentid,
        patientid: data?.patientid,
        notes: "testing data",
        tags: "tagsdata",
        patientname: `${data?.firstname} ${data?.lastname}`,
        role: "PRO",
        filename: streamNameRef.current,
        streamingfilename: streamFilenameRef.current,
        appointmentid: data?.appointmentid,
        userid: user?.id,
        templatename: "",
        // Carried to the upload: the consent was given for this recording.
        past_visit_confirmed: pastVisitConfirmedRef.current,
        client: "care",
      };

      if (combineStatusRef.current) {
        obj.rowid = latestAppRef.current?.id;
        obj.category = "combine";
      }

      saveAudioInIndexDB(obj);
    }
  }, []);

  // Register manual recording hooks from SideFrame
  const registerManualRecording = useCallback(({ manualrecord, stopmanualRecording, pausemanualRecording, resumemanualRecording }) => {
    manualRecordRef.current = manualrecord;
    stopManualRecordingRef.current = stopmanualRecording;
    pauseManualRecordingRef.current = pausemanualRecording;
    resumeManualRecordingRef.current = resumemanualRecording;
  }, []);

  // Reset all recording/processing state — call when switching patients
  const clearLastNote = useCallback(() => {
    // The previous patient's completion poll is moot once processing is
    // cleared: stop it so it can't later toast "still processing" onto
    // another patient's chart or keep polling in the background.
    cancelCompletionPoll();
    if (processingTimerRef.current) {
      clearInterval(processingTimerRef.current);
      processingTimerRef.current = null;
    }
    setLastSOAPNote(null);
    setLastTranscript("");
    setLastNotePatientId(null);
    setLastCompletedRecording(null);
    setIsProcessing(false);
    setProcessingStage("");
    setProcessingPercent(0);
    setLiveSOAPNote(null);
    setLiveTranscript("");
    setSuggestedICD10([]);
    setSuggestedOrders([]);
    setOrderSuggestionsV2(null);
    setVoiceOrders(null);
    setLiveOrders(null);
    setCareIQAlerts([]);
    setToolCallCount(0);
  }, [cancelCompletionPoll]);

  const value = {
    // UI State
    isGlobalRecording,
    setIsGlobalRecording,
    isGlobalPaused,
    setIsGlobalPaused,
    globalElapsedTime,
    setGlobalElapsedTime,
    patientInfo,
    setPatientInfo,
    showFloatingPopup,
    setShowFloatingPopup,
    isOnRecordingPage,
    setIsOnRecordingPage,

    // Recording infrastructure refs
    wsRef,
    recorderRef,
    audioStreamRef,
    pausedRef,
    usedPauseRef,
    isManualStopRef,
    isManualRecordStartRef,
    blockerRef,
    combineStatusRef,
    latestAppRef,
    streamNameRef,
    streamFilenameRef,
    userRef,
    patientDataRef,

    // Core functions
    startRecording,
    stopRecording,
    togglePause,
    onSave,
    onDelete,
    submitAudioBlobManually,
    startTimer,
    stopTimer,

    // Registration
    registerManualRecording,
    showToastRef,
    setPollTriggerRef,
    clearLastNote,

    // Live Scribe state (Passive Scribe)
    liveSOAPNote,
    liveTranscript,
    suggestedICD10,
    suggestedOrders,
    orderSuggestionsV2,
    voiceOrders,
    liveOrders,
    careIQAlerts,
    toolCallCount,
    liveScribeHealthy,

    // Server-pushed recording resilience signals + long-recording prompt
    recordingStatus,
    setRecordingStatus,
    showLongRecordingPrompt,
    // Provider clicked "Yes, continue" — close the modal and reset the 45-min timer
    confirmStillRecording: () => {
      lastLongPromptAtRef.current = Date.now();
      setShowLongRecordingPrompt(false);
    },
    // Provider clicked "Stop" in the modal — close the modal (caller should also call stopRecording)
    dismissLongRecordingPrompt: () => setShowLongRecordingPrompt(false),

    // Post-recording processing state
    isProcessing,
    processingStage,
    processingPercent,
    lastSOAPNote,
    lastTranscript,
    lastNotePatientId,
    lastCompletedRecording,

    // The visit the live recording is filed to (classifyVisit shape).
    recordingVisit,
  };

  return (
    <RecordingContext.Provider value={value}>
      {children}
    </RecordingContext.Provider>
  );
};

export default RecordingContext;
